import { formatMonthKey } from '../reportUtils';
import { CHART_PALETTE, STATUS_CHART_COLORS } from './chartTheme';

export function labeledPieData(rows, { labelKey, valueKey = 'count', labelMap = {}, colorMap = {} } = {}) {
  const labels = rows.map((row) => labelMap[row[labelKey]] || row[labelKey]);
  const values = rows.map((row) => Number(row[valueKey]));
  const colors = rows.map((row, i) => colorMap[row[labelKey]] || CHART_PALETTE[i % CHART_PALETTE.length]);
  return { labels, values, colors };
}

export function truncateLabel(label, max = 32) {
  const text = String(label || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

export function computeShares(rows, valueKey) {
  const total = rows.reduce((sum, row) => sum + Number(row[valueKey] || 0), 0);
  let cumulative = 0;
  return rows.map((row) => {
    const value = Number(row[valueKey] || 0);
    const sharePct = total > 0 ? (value / total) * 100 : 0;
    cumulative += sharePct;
    return {
      ...row,
      share_pct: sharePct,
      cumulative_pct: cumulative
    };
  });
}

export function topNWithOthers(rows, valueKey, labelKey, n = 5, othersLabel = 'Autres') {
  if (!rows.length) return { labels: [], values: [], colors: [] };
  const sorted = [...rows].sort((a, b) => Number(b[valueKey]) - Number(a[valueKey]));
  const head = sorted.slice(0, n);
  const tail = sorted.slice(n);
  const tailSum = tail.reduce((s, r) => s + Number(r[valueKey] || 0), 0);

  const labels = head.map((r) => truncateLabel(r[labelKey], 24));
  const values = head.map((r) => Number(r[valueKey] || 0));
  const colors = head.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]);

  if (tailSum > 0) {
    labels.push(othersLabel);
    values.push(tailSum);
    colors.push('#cbd5e1');
  }

  return { labels, values, colors };
}

export function mergeMonthlyTrend(consultationMonths = [], prescriptionMonths = []) {
  const map = new Map();

  consultationMonths.forEach((row) => {
    map.set(row.month_key, { month_key: row.month_key, consultations: Number(row.count), prescriptions: 0 });
  });

  prescriptionMonths.forEach((row) => {
    const existing = map.get(row.month_key) || {
      month_key: row.month_key,
      consultations: 0,
      prescriptions: 0
    };
    existing.prescriptions = Number(row.count);
    map.set(row.month_key, existing);
  });

  const sorted = [...map.values()].sort((a, b) => String(a.month_key).localeCompare(String(b.month_key)));

  return {
    labels: sorted.map((row) => formatMonthKey(row.month_key)),
    consultations: sorted.map((row) => row.consultations),
    prescriptions: sorted.map((row) => row.prescriptions)
  };
}

export function statusPieData(byStatus = [], labelMap = {}) {
  return labeledPieData(byStatus, {
    labelKey: 'status',
    labelMap,
    colorMap: STATUS_CHART_COLORS
  });
}

export function barChartData(labels, values, label, color = CHART_PALETTE[0]) {
  return {
    labels,
    datasets: [
      {
        label,
        data: values,
        backgroundColor: color,
        borderRadius: 6,
        maxBarThickness: horizontalBarThickness(labels.length)
      }
    ]
  };
}

/** Histogramme vertical style Matplotlib (barres jointives, coins droits). */
export function histogramChartData(labels, values, label, color = CHART_PALETTE[0]) {
  return {
    labels,
    datasets: [
      {
        label,
        data: values,
        backgroundColor: `${color}b3`,
        borderColor: color,
        borderWidth: 1.25,
        borderSkipped: false,
        borderRadius: 0,
        categoryPercentage: 1,
        barPercentage: 0.95,
        maxBarThickness: 56
      }
    ]
  };
}

export function horizontalBarChartData(labels, values, label, colors) {
  return {
    labels,
    datasets: [
      {
        label,
        data: values,
        backgroundColor: colors || values.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]),
        borderRadius: 6,
        maxBarThickness: 28
      }
    ]
  };
}

/**
 * Transforme des séries mensuelles (disease_id, disease_name, consultation_count)
 * en données boxplot (une distribution par pathologie).
 */
export function pathologyBoxplotChartData(series = [], rankingRows = [], label = 'Consultations / mois') {
  const byId = new Map();
  series.forEach((row) => {
    const id = row.disease_id;
    if (!byId.has(id)) {
      byId.set(id, {
        disease_id: id,
        disease_name: row.disease_name,
        values: []
      });
    }
    byId.get(id).values.push(Number(row.consultation_count || 0));
  });

  const orderedIds =
    rankingRows.length > 0
      ? rankingRows.map((r) => r.disease_id).filter((id) => byId.has(id))
      : [...byId.keys()];

  let ordered = orderedIds.map((id) => byId.get(id)).filter(Boolean).slice(0, 10);

  // Période courte / fallback : un point = total consultations de la pathologie
  if (ordered.length === 0 && rankingRows.length > 0) {
    ordered = rankingRows.slice(0, 10).map((row) => ({
      disease_id: row.disease_id,
      disease_name: row.disease_name,
      values: [Number(row.consultation_count || 0)]
    }));
  }

  return {
    labels: ordered.map((d) => truncateLabel(d.disease_name, 24)),
    datasets: [
      {
        label,
        backgroundColor: `${CHART_PALETTE[0]}55`,
        borderColor: CHART_PALETTE[0],
        borderWidth: 1.5,
        outlierBackgroundColor: CHART_PALETTE[2],
        outlierBorderColor: CHART_PALETTE[2],
        medianColor: CHART_PALETTE[4] || '#16a34a',
        itemRadius: 2,
        data: ordered.map((d) => d.values)
      }
    ]
  };
}

export function lineChartData(labels, series) {
  return {
    labels,
    datasets: series.map((s, i) => ({
      label: s.label,
      data: s.data,
      borderColor: s.color || CHART_PALETTE[i % CHART_PALETTE.length],
      backgroundColor: `${s.color || CHART_PALETTE[i % CHART_PALETTE.length]}22`,
      fill: s.fill ?? false,
      tension: 0.35,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2.5,
      yAxisID: s.yAxisID || 'y'
    }))
  };
}

export function doughnutChartData(labels, values, colors) {
  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 6
      }
    ]
  };
}

function horizontalBarThickness(count) {
  if (count <= 5) return 36;
  if (count <= 10) return 28;
  return 22;
}

export function formatPercentCell(value) {
  return `${Number(value || 0).toFixed(1).replace('.', ',')} %`;
}
